import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Interruptor, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { etiquetaDeNumerador, type Numerador } from './NumeradoresDeComprobante';

interface Tipo {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly signo: number;
  readonly mueveStock: 'ninguno' | 'descuenta' | 'ingresa';
  readonly depositoId: string | null;
  readonly esValorizado: boolean;
  readonly haceIva: boolean;
  readonly haceContabilidad: boolean;
  readonly tocaCaja: boolean;
  readonly afectaCuenta: boolean;
  readonly pideCae: boolean;
  readonly numeraPorPunto: boolean;
  readonly listaDePreciosId: string | null;
  readonly listaEsFija: boolean;
  readonly requiereOrigen: boolean;
  readonly numeradorId: string | null;
  readonly puestoDeCajaId: string | null;
  readonly activo: boolean;
  /** Si el rol de quien mira puede emitir con este tipo. Lo decide el servidor. */
  readonly puedeUsar: boolean;
}

/** Las banderas que se dibujan como insignia, en el orden en que alguien las piensa. */
const BANDERAS = [
  'esValorizado',
  'haceIva',
  'haceContabilidad',
  'tocaCaja',
  'afectaCuenta',
  'pideCae',
] as const;

/**
 * El formulario, tipado.
 *
 * La primera versión usaba `Record<string, unknown>` y el lint tenía razón en rechazarlo: perdía
 * los tipos justo donde se arma el cuerpo que se manda al servidor, que es donde más importan.
 */
interface Formulario {
  codigo: string;
  nombre: string;
  signo: number;
  mueveStock: 'ninguno' | 'descuenta' | 'ingresa';
  esValorizado: boolean;
  haceIva: boolean;
  haceContabilidad: boolean;
  tocaCaja: boolean;
  afectaCuenta: boolean;
  pideCae: boolean;
  numeraPorPunto: boolean;
  listaDePreciosId: string;
  listaEsFija: boolean;
  requiereOrigen: boolean;
  numeradorId: string;
  puestoDeCajaId: string;
}

const NUEVO: Formulario = {
  codigo: '',
  nombre: '',
  signo: 1,
  mueveStock: 'ninguno' as const,
  esValorizado: true,
  haceIva: false,
  haceContabilidad: false,
  tocaCaja: false,
  afectaCuenta: false,
  pideCae: false,
  numeraPorPunto: true,
  listaDePreciosId: '',
  listaEsFija: false,
  requiereOrigen: false,
  numeradorId: '',
  puestoDeCajaId: '',
};

/**
 * Los tipos de comprobante, con su comportamiento como dato.
 *
 * ## Lo que esta pantalla tiene que dejar claro
 *
 * **Qué hace cada tipo, sin abrirlo.** La lista muestra las banderas encendidas como insignias, no
 * una columna por bandera: nueve columnas de sí y no son ilegibles, y lo que alguien quiere saber
 * de un vistazo es «éste declara IVA y aquél no».
 *
 * Y **qué no se puede configurar**: la letra del comprobante no está y no va a estar. Sale de la
 * RG 1415 según la condición del emisor y la del receptor. Un comercio puede inventar «Remito de
 * obra»; no puede inventar que sea letra A. Se dice en la pantalla para que nadie la busque.
 *
 * ## Por qué el servidor rechaza cosas que el formulario deja marcar
 *
 * Se deja marcar cualquier combinación y se explica el rechazo, en vez de deshabilitar casillas.
 * Deshabilitar esconde la regla: alguien ve una casilla gris y no sabe si es un permiso que le
 * falta, un defecto o una regla. Un mensaje que dice «declarar IVA exige llevar importes» enseña.
 */
export function TiposDeComprobante(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeAdministrar = usePermiso('VENTAS.TIPO_DE_COMPROBANTE.ADMINISTRAR');

  const [creando, setCreando] = useState(false);
  const [formulario, setFormulario] = useState<Formulario>({ ...NUEVO });
  const [corrigiendo, setCorrigiendo] = useState<Tipo | null>(null);
  /** De qué tipos puede nacer el que se está mirando. Nulo con el diálogo cerrado. */
  const [armandoCircuito, setArmandoCircuito] = useState<Tipo | null>(null);
  const [origenes, setOrigenes] = useState<string[]>([]);
  /** Cuáles de esos orígenes quedan pendientes hasta que exista el destino. */
  const [dejanPendiente, setDejanPendiente] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const tipos = useQuery({
    queryKey: ['tipos-de-comprobante'],
    queryFn: () => pedir<Tipo[]>('/tipos-de-comprobante'),
  });

  const numeradores = useQuery({
    queryKey: ['numeradores'],
    queryFn: () => pedir<Numerador[]>('/numeradores'),
  });

  const puestos = useQuery({
    queryKey: ['puestos-de-caja'],
    queryFn: () => pedir<{ id: string; nombre: string }[]>('/puestos-de-caja'),
  });

  const listas = useQuery({
    queryKey: ['listas-de-precios'],
    queryFn: () => pedir<{ id: string; nombre: string }[]>('/listas-de-precios'),
  });
  const circuito = useQuery({
    queryKey: ['circuito'],
    queryFn: () =>
      pedir<{ destinoId: string; origenId: string; dejaPendiente: boolean }[]>(
        '/tipos-de-comprobante/circuito',
      ),
  });

  const fijarCircuito = useMutation({
    mutationFn: () =>
      pedir<{ origenes: number }>(`/tipos-de-comprobante/${armandoCircuito?.id ?? ''}/origenes`, {
        method: 'POST',
        cuerpo: { origenes, dejanPendiente },
      }),
    onSuccess: async () => {
      setArmandoCircuito(null);
      setError(null);
      await cola.invalidateQueries({ queryKey: ['circuito'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const refrescar = async (): Promise<void> => {
    setError(null);
    setCreando(false);
    setCorrigiendo(null);
    await cola.invalidateQueries({ queryKey: ['tipos-de-comprobante'] });
  };

  /**
   * El formulario, listo para viajar.
   *
   * El selector vacío significa «la que el comercio tenga puesta», y eso en la ruta se dice con
   * nulo: mandar la cadena vacía sería mandar un identificador que no existe.
   */
  const paraMandar = (datos: Formulario): Record<string, unknown> => ({
    ...datos,
    listaDePreciosId: datos.listaDePreciosId === '' ? null : datos.listaDePreciosId,
    // Vacío es «el de su familia fiscal» y «la que elija quien vende»; en la ruta eso es nulo.
    numeradorId: datos.numeradorId === '' ? null : datos.numeradorId,
    puestoDeCajaId: datos.puestoDeCajaId === '' ? null : datos.puestoDeCajaId,
  });

  const crear = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/tipos-de-comprobante', {
        method: 'POST',
        cuerpo: paraMandar(formulario),
      }),
    onSuccess: refrescar,
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const corregir = useMutation({
    mutationFn: (cambios: Formulario) =>
      pedir<{ id: string }>(`/tipos-de-comprobante/${corrigiendo?.id ?? ''}`, {
        method: 'PATCH',
        cuerpo: paraMandar(cambios),
      }),
    onSuccess: refrescar,
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const alternar = useMutation({
    mutationFn: (tipo: Tipo) =>
      pedir<{ id: string }>(`/tipos-de-comprobante/${tipo.id}`, {
        method: 'PATCH',
        cuerpo: { activo: !tipo.activo },
      }),
    onSuccess: refrescar,
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  if (tipos.isPending) return <Cargando />;
  if (tipos.isError) return <Problema error={tipos.error} />;

  const columnas: Columna<Tipo>[] = [
    {
      clave: 'codigo',
      titulo: t('tiposDeComprobante.codigo'),
      contenido: (fila) => <span className="numero">{fila.codigo}</span>,
    },
    { clave: 'nombre', titulo: t('tiposDeComprobante.nombre'), contenido: (fila) => fila.nombre },
    {
      clave: 'signo',
      titulo: t('tiposDeComprobante.signo'),
      contenido: (fila) => (
        <Insignia tono={fila.signo === 0 ? 'neutro' : fila.signo > 0 ? 'bien' : 'atencion'}>
          {t(
            fila.signo === 0
              ? 'tiposDeComprobante.noImpacta'
              : fila.signo > 0
                ? 'tiposDeComprobante.suma'
                : 'tiposDeComprobante.resta',
          )}
        </Insignia>
      ),
    },
    {
      clave: 'que',
      titulo: t('tiposDeComprobante.queHace'),
      // Las banderas encendidas, no una columna por bandera: nueve columnas de sí y no son
      // ilegibles, y lo que se quiere saber de un vistazo es qué hace este tipo y aquél no.
      contenido: (fila) => (
        <div className="flex flex-wrap gap-1">
          {BANDERAS.filter((bandera) => fila[bandera]).map((bandera) => (
            <Insignia key={bandera}>{t(`tiposDeComprobante.${bandera}`)}</Insignia>
          ))}
          {fila.mueveStock === 'ninguno' ? null : (
            <Insignia tono="atencion">{t(`tiposDeComprobante.${fila.mueveStock}`)}</Insignia>
          )}
        </div>
      ),
    },
    {
      clave: 'estado',
      titulo: t('tiposDeComprobante.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activo ? 'bien' : 'neutro'}>
          {t(fila.activo ? 'tiposDeComprobante.enUso' : 'tiposDeComprobante.apagado')}
        </Insignia>
      ),
    },
    ...(puedeAdministrar
      ? [
          {
            clave: 'acciones',
            titulo: '',
            contenido: (fila: Tipo) => (
              <div className="flex justify-end gap-1">
                <Boton
                  onClick={() => {
                    setCorrigiendo(fila);
                    // Sólo los campos del formulario: mandar de vuelta `id`, `activo` o
                    // `depositoId` a un esquema estricto es un error, y con razón.
                    setFormulario({
                      codigo: fila.codigo,
                      nombre: fila.nombre,
                      signo: fila.signo,
                      mueveStock: fila.mueveStock,
                      esValorizado: fila.esValorizado,
                      haceIva: fila.haceIva,
                      haceContabilidad: fila.haceContabilidad,
                      tocaCaja: fila.tocaCaja,
                      afectaCuenta: fila.afectaCuenta,
                      pideCae: fila.pideCae,
                      numeraPorPunto: fila.numeraPorPunto,
                      listaDePreciosId: fila.listaDePreciosId ?? '',
                      listaEsFija: fila.listaEsFija,
                      requiereOrigen: fila.requiereOrigen,
                      numeradorId: fila.numeradorId ?? '',
                      puestoDeCajaId: fila.puestoDeCajaId ?? '',
                    });
                    setError(null);
                  }}
                >
                  {t('acciones.corregir')}
                </Boton>
                <Boton
                  onClick={() => {
                    setArmandoCircuito(fila);
                    const suyas = (circuito.data ?? []).filter(
                      (conversion) => conversion.destinoId === fila.id,
                    );
                    setOrigenes(suyas.map((conversion) => conversion.origenId));
                    setDejanPendiente(
                      suyas
                        .filter((conversion) => conversion.dejaPendiente)
                        .map((conversion) => conversion.origenId),
                    );
                    setError(null);
                  }}
                >
                  {t('tiposDeComprobante.circuito')}
                </Boton>
                <Boton
                  cargando={alternar.isPending}
                  onClick={() => {
                    alternar.mutate(fila);
                  }}
                >
                  {t(fila.activo ? 'tiposDeComprobante.apagar' : 'tiposDeComprobante.encender')}
                </Boton>
              </div>
            ),
          },
        ]
      : []),
  ];

  const campos = (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Campo
          etiqueta={t('tiposDeComprobante.codigo')}
          value={formulario.codigo}
          onChange={(evento) => {
            setFormulario({ ...formulario, codigo: evento.target.value });
          }}
        />
        <Campo
          etiqueta={t('tiposDeComprobante.nombre')}
          value={formulario.nombre}
          onChange={(evento) => {
            setFormulario({ ...formulario, nombre: evento.target.value });
          }}
        />
      </div>
      <Selector
        etiqueta={t('tiposDeComprobante.signo')}
        value={String(formulario.signo)}
        onChange={(evento) => {
          setFormulario({ ...formulario, signo: Number(evento.target.value) });
        }}
      >
        <option value="1">{t('tiposDeComprobante.suma')}</option>
        <option value="-1">{t('tiposDeComprobante.resta')}</option>
        <option value="0">{t('tiposDeComprobante.noImpacta')}</option>
      </Selector>
      <Selector
        etiqueta={t('tiposDeComprobante.mueveStock')}
        value={formulario.mueveStock}
        onChange={(evento) => {
          setFormulario({
            ...formulario,
            mueveStock: evento.target.value as Formulario['mueveStock'],
          });
        }}
      >
        <option value="ninguno">{t('tiposDeComprobante.ninguno')}</option>
        <option value="descuenta">{t('tiposDeComprobante.descuenta')}</option>
        <option value="ingresa">{t('tiposDeComprobante.ingresa')}</option>
      </Selector>
      {/* El numerador se elige de los que existen, no se escribe: es una entidad con nombre y
          punto de venta, y tipearlo a mano volvería a la serie suelta que E26 vino a sacar. */}
      <Selector
        etiqueta={t('tiposDeComprobante.numerador')}
        value={formulario.numeradorId}
        ayuda={t('tiposDeComprobante.numeradorAyuda')}
        onChange={(evento) => {
          setFormulario({ ...formulario, numeradorId: evento.target.value });
        }}
      >
        <option value="">{t('tiposDeComprobante.elDeSuFamilia')}</option>
        {(numeradores.data ?? [])
          .filter((numerador) => numerador.activo)
          .map((numerador) => (
            <option key={numerador.id} value={numerador.id}>
              {etiquetaDeNumerador(numerador.puntoDeVenta, numerador.nombre)}
            </option>
          ))}
      </Selector>

      {/* La caja: si el tipo la fija, quien vende no la elige. Misma forma que la lista fija. */}
      <Selector
        etiqueta={t('tiposDeComprobante.puestoDeCaja')}
        value={formulario.puestoDeCajaId}
        ayuda={t('tiposDeComprobante.puestoDeCajaAyuda')}
        onChange={(evento) => {
          setFormulario({ ...formulario, puestoDeCajaId: evento.target.value });
        }}
      >
        <option value="">{t('tiposDeComprobante.laQueElijaQuienVende')}</option>
        {(puestos.data ?? []).map((puesto) => (
          <option key={puesto.id} value={puesto.id}>
            {puesto.nombre}
          </option>
        ))}
      </Selector>
      {/* La lista va arriba de las banderas y no entre ellas: no es un sí o un no, es con qué
          precio se vende, y el interruptor que la fija sólo tiene sentido si ya se eligió una. */}
      <Selector
        etiqueta={t('tiposDeComprobante.listaDePrecios')}
        value={formulario.listaDePreciosId}
        ayuda={t('tiposDeComprobante.listaDePreciosAyuda')}
        onChange={(evento) => {
          setFormulario({ ...formulario, listaDePreciosId: evento.target.value });
        }}
      >
        <option value="">{t('tiposDeComprobante.laQueElComercioTenga')}</option>
        {(listas.data ?? []).map((lista) => (
          <option key={lista.id} value={lista.id}>
            {lista.nombre}
          </option>
        ))}
      </Selector>
      <div className="grid gap-2 sm:grid-cols-2">
        {(
          [
            ['esValorizado', 'esValorizadoAyuda'],
            ['haceIva', 'haceIvaAyuda'],
            ['haceContabilidad', 'haceContabilidadAyuda'],
            ['tocaCaja', 'tocaCajaAyuda'],
            ['afectaCuenta', 'afectaCuentaAyuda'],
            ['pideCae', 'pideCaeAyuda'],
            ['numeraPorPunto', 'numeraPorPuntoAyuda'],
            ['listaEsFija', 'listaEsFijaAyuda'],
            ['requiereOrigen', 'requiereOrigenAyuda'],
          ] as const
        ).map(([bandera, ayuda]) => (
          <Interruptor
            key={bandera}
            etiqueta={t(`tiposDeComprobante.${bandera}`)}
            ayuda={t(`tiposDeComprobante.${ayuda}`)}
            checked={formulario[bandera]}
            onChange={(evento) => {
              setFormulario({ ...formulario, [bandera]: evento.target.checked });
            }}
          />
        ))}
      </div>
      {/* La letra no está y no va a estar: sale de la norma, no de la configuración. */}
      <p className="text-apoyo text-texto-tenue">{t('tiposDeComprobante.laLetraNoSeElige')}</p>
      {error === null ? null : (
        <p role="alert" className="text-apoyo text-mal">
          {error}
        </p>
      )}
    </div>
  );

  return (
    <>
      <Encabezado
        titulo={t('tiposDeComprobante.titulo')}
        descripcion={t('tiposDeComprobante.descripcion')}
        acciones={
          puedeAdministrar ? (
            <Boton
              tono="principal"
              onClick={() => {
                setFormulario({ ...NUEVO });
                setError(null);
                setCreando(true);
              }}
            >
              {t('tiposDeComprobante.nuevo')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        <Tabla filas={tipos.data} columnas={columnas} claveDeFila={(fila) => fila.id} />
      </Tarjeta>

      <Dialogo
        abierto={creando}
        titulo={t('tiposDeComprobante.nuevo')}
        onCerrar={() => {
          setCreando(false);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        {campos}
      </Dialogo>

      {/*
        El circuito. Se elige la lista entera —de qué tipos puede nacer éste— y no se agrega de a
        uno: es una decisión completa que alguien mira y define, no una colección que crece.
      */}
      <Dialogo
        abierto={armandoCircuito !== null}
        titulo={t('tiposDeComprobante.deQueNace', { nombre: armandoCircuito?.nombre ?? '' })}
        descripcion={t('tiposDeComprobante.circuitoAyuda')}
        onCerrar={() => {
          setArmandoCircuito(null);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={fijarCircuito.isPending}
            onClick={() => {
              fijarCircuito.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-2">
          {tipos.data
            .filter((otro) => otro.id !== armandoCircuito?.id)
            .map((otro) => (
              <div key={otro.id}>
                <Interruptor
                  etiqueta={otro.nombre}
                  checked={origenes.includes(otro.id)}
                  onChange={(evento) => {
                    setOrigenes((previos) =>
                      evento.target.checked
                        ? [...previos, otro.id]
                        : previos.filter((id) => id !== otro.id),
                    );
                    // Al sacar el origen se saca también su pendiente: dejar marcado que algo
                    // queda pendiente de una relación que ya no existe es guardar una regla que
                    // nadie va a poder cumplir.
                    if (!evento.target.checked) {
                      setDejanPendiente((previos) => previos.filter((id) => id !== otro.id));
                    }
                  }}
                />
                {/* Sangrado y sólo cuando el origen está marcado: «queda pendiente» es una
                    propiedad de la relación, no del tipo, y sin relación no significa nada. */}
                {origenes.includes(otro.id) ? (
                  <div className="ml-6 mt-1">
                    <Interruptor
                      etiqueta={t('tiposDeComprobante.dejaPendiente')}
                      ayuda={t('tiposDeComprobante.dejaPendienteAyuda')}
                      checked={dejanPendiente.includes(otro.id)}
                      onChange={(evento) => {
                        setDejanPendiente((previos) =>
                          evento.target.checked
                            ? [...previos, otro.id]
                            : previos.filter((id) => id !== otro.id),
                        );
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={corrigiendo !== null}
        titulo={t('tiposDeComprobante.corregir')}
        descripcion={t('tiposDeComprobante.rigeDesdeAhora')}
        onCerrar={() => {
          setCorrigiendo(null);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={corregir.isPending}
            onClick={() => {
              corregir.mutate(formulario);
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        {campos}
      </Dialogo>
    </>
  );
}
